<template>
  <v-app>
    <v-main>  
      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>
      <Loading />
      <Toast />
      <Confirm />
      <MessageDialog />
      <FixedButton/>
    </v-main>
  </v-app>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router';
import { isPcWeb, goStoreTtobakCare } from '@/utils/common';
import { useHead } from 'nuxt/app';

import Loading from '@/components/Loading.vue';
import Toast from '@/components/Toast.vue';
import Confirm from '@/components/Confirm.vue';
import MessageDialog from '@/components/MessageDialog.vue';
import FixedButton from './components/store/FixedButton.vue';

const router = useRouter();
const rootStore = useRootStore();
const authStore = useAuthStore();
const memberStore = useMemberStore();
const { $getCookie, $getAccessToken, $isSSR } = useNuxtApp();

const url = useRequestURL();
const appSessId = $getCookie('APPSESSID');
/**
 * 앱에서 생성된 쿠키 APPSESSID 값을 웹에서 쿠키 APP_SESSID으로 다시 생성됨
 * - APPSESSID 
 *   앱에서 생성된 쿠키로 secure : false, sameSite: 'lax' 로 설정되어 되어 있어
 *   PG 결제 페이지에서 취소 버튼을 누를 경우 주문 페이지에서 APPSESSID 값을 읽을 수 없는 문제가 발생함
 * - APP_SESSID
 *   결제창에서 취소할 경우에 사용할 쿠키값(token)
 */
const appSessId2 = $getCookie('APP_SESSID');
const refreshToken = $getCookie('REFRESH_TOKEN');

const config = useRuntimeConfig();
const event = useRequestEvent();
const referer = import.meta.server ? event?.node.req.headers?.referer : document.referrer;

/**
 * token 및 auth store 설정 (최초 로딩할 때)
 */
if (($isSSR && import.meta.server) || !$isSSR) {
  if (isMobileApp()) {
    if (appSessId) {
      authStore.saveAuthenToken(appSessId);
    } else {
      if (appSessId2) {
        // referer가 PG사 도메인일 경우에만 APP_SESSID 쿠키 사용
        if (referer?.startsWith(config.public.VITE_PAYMENT_PG_URL)) {
          authStore.saveAuthenToken(appSessId2);
        } else {
          removeLoginSessionId();
        }
      } else {
        removeLoginSessionId();
      }
    }
  } else {
    if (refreshToken) {
      //console.log('===== getAccessToken in app.vue =====');
      //console.log('VITE_ENV_TYPE', config.public.VITE_ENV_TYPE);
      
      await $getAccessToken();
      //console.log('===== getAccessToken in app.vue(end) =====');
    } else {
      removeLoginSessionId();
    }
  }
}

useHead({
  htmlAttrs: {
    lang: 'ko'
  },
  meta: [
    { property: 'og:url', content: url.origin },
  ],
})

// 회원정보 업데이트 (token 변경할 때)
watch(
  () => authStore.authToken,
  async (newVal) => {
    await loadMemberInfo(authStore.isFirstLogin);
    authStore.isFirstLogin = false;
  }
);

onBeforeMount(() => {
  // 외부 script 로딩 (HEAD)
  preLoadScript();
});

onMounted(async () => {


  //console.log('appSessId equal', appSessId === '');
        
  // 회원정보 업데이트
  await loadMemberInfo();
  // 분석 스크립트 로딩
  loadAnalyticsScript();
  // 페이지 스크립트 로딩
  pageInit();
  // 앱 로그인 후처리
  appResponseLogin();
  // 앱 로그아웃 후처리
  appResponseLogout();
  
  router.beforeEach(async (to, from, next) => {
    // 공통 컴포넌트 초기화(로딩, confirm, message box, 헤더 영역)
    rootStore.hideLoading();
    rootStore.resetConfirm();
    rootStore.resetMessageDialog();
    rootStore.resetHeaderTitle();
    rootStore.setFooter(true);
    
    // 사용자 인증 데이터 설정 (페이지 이동 시)
    await pageAuth();
    next();
  });
  
  router.afterEach((to, from) => {
    pageInit(to);
  });
});

/**
 * 회원정보 업데이트
 */
const loadMemberInfo = async (isFirstLogin: boolean = false): Promise<void> => {
  if (!authStore.isLogin) {
    memberStore.clearMemberInfo();
    return;
  }
  
  await getMemberInfo(false, isFirstLogin);
};
</script>

<style lang="scss">
  @import 'bootstrap/scss/bootstrap';
  @import 'roboto-fontface/css/roboto/roboto-fontface.css';
  @import '@/styles/common.css';
  @import '@/styles/clinic.css';
  @import '@/styles/event.css';
  @import '@/styles/mypage.css';
  @import '@/styles/order.css';
  :root {
    --bs-body-font-size: var(--hecto-font-32);
  }
</style>

<style lang="scss">
  @use '@/assets/scss/common.scss'; // 공통 스타일(2025-03-19 리뉴얼)
</style>